import _ from "lodash";
import { FilterExpressionParser } from "../antlr/FilterExpressionParser.js";
import { FilterExpressionVariantListener } from "./FilterExpressionVariantListener.js";
import { ParseTreeWalker } from "antlr4ts/tree/ParseTreeWalker";
import { FilterEvaluator } from "./FilterEvaluator.js";
import { FilterExpressionOperatorsListener } from "./FilterExpressionOperatorsListener.js";
import { FilterRangesVisitor } from "./FilterRangesVisitor.js";
import { tryGetString, tryGetBoolean, tryGetVersion, tryGetInteger } from "./FilterExpressionParser.utils.js";
import { ANTLRInputStream, CommonTokenStream } from "antlr4ts";
import { FilterExpressionLexer } from "../antlr/FilterExpressionLexer.js";
import { stringCompareTo, booleanCompareTo, versionCompareTo, numberCompareTo } from "../../common/utils/common.utils.js";
class FilterExpression {
  /// <summary>
  /// Parse a filter set expression with basic validation.
  /// </summary>
  /// <param name="filterExpression">Filter expression.</param>
  /// <returns>Parse tree.</returns>
  static parse(filterExpression) {
    const parser = this.getParser(filterExpression);
    try {
      const parseTree = parser.filterSet();
      return parseTree;
    } catch (e) {
      throw new Error(`Cannot parse audience filter expression: ${filterExpression}`);
    }
  }
  /// <summary>
  /// Validates the filter expression and returns errors if there are any.
  /// </summary>
  /// <param name="parseTree">Filter expression parse tree.</param>
  /// <param name="errors">(Optional) Collection where errors are accumulated.</param>
  /// <returns>Map from varint name to its filter expression parse tree.</returns>
  getVariantFilterExpressions(parseTree, errors = []) {
    const filterExprContexts = parseTree.filterExpr();
    const results = {};
    _.each(filterExprContexts, (filterExpr) => {
      const listener = new FilterExpressionVariantListener();
      ParseTreeWalker.DEFAULT.walk(listener, filterExpr);
      _.each(Object.keys(listener.variantTypes), (variant) => {
        if (!!results[variant]) {
          errors.push(new Error(`Audience filter "${variant}" is used in multiple filter expressions.`));
        } else {
          results[variant] = filterExpr;
        }
      });
      if (Object.keys(listener.variantTypes).length > 1) {
        errors.push(new Error(`Different audience filters cannot be combined with logical operators.`));
      }
    });
    return results;
  }
  /// <summary>
  /// Determines if a set of variants matches a filter expression.
  /// </summary>
  /// <param name="parseTree">Filter expression parse tree.</param>
  /// <param name="variants">Variant values (on left hand side of operators).</param>
  /// <returns>True if the input is valid filter expression, otherwise false.</returns>
  static isMatch(parseTree, variants) {
    const visitor = new FilterEvaluator(variants);
    const isMatch = parseTree.accept(visitor);
    return isMatch;
  }
  /// <summary>
  /// Determines if a set of variants matches a filter expression.
  /// </summary>
  /// <param name="parseTree">Filter expression parse tree.</param>
  /// <param name="variants">Variant values (on left hand side of operators).</param>
  /// <returns>True if the input is valid filter expression, otherwise false.</returns>
  static isMatchInner(parseTree, variants) {
    const variantsDictionary = {};
    _.each(variants, (v) => variantsDictionary[v.name.toLowerCase()] = v.value);
    const visitor = new FilterEvaluator(variantsDictionary);
    const isMatch = parseTree.accept(visitor);
    return isMatch;
  }
  /// <summary>
  /// Flattens a filter expression to the set of included string values.
  /// </summary>
  /// <param name="parseTree">Parsed filter set expression.</param>
  /// <param name="valuesIncluded">Indicates whether the filter expression includes or excludes the returned values.</param>
  /// <param name="errors">(Optional) Collection where errors are accumulated.</param>
  /// <returns>Sequence of values included or excluded by the filter expression.</returns>
  static getStringValues(parseTree, errors = null) {
    var _a, _b;
    if (parseTree == null) {
      throw new Error(`Parse tree cannot be null.`);
    }
    const operators = new FilterExpressionOperatorsListener();
    ParseTreeWalker.DEFAULT.walk(operators, parseTree);
    if (operators.rangeOperatorsCount > 0) {
      errors = this.addOrThrow(errors, "String filter expressions cannot use range operators.");
    }
    if (operators.equalOperatorsCount > 0 && operators.notEqualsOperatorsCount > 0) {
      errors = this.addOrThrow(errors, "String filter expression cannot use both 'IN' and 'NOTIN' operators.");
    }
    const visitor = new FilterRangesVisitor(tryGetString, stringCompareTo, []);
    const ranges = parseTree.accept(visitor);
    errors = this.addOrThrowMultiple(errors, visitor.errors);
    if (ranges.every((x) => {
      var _a2;
      return x.lessThan !== null && ((_a2 = x.greaterThan) == null ? void 0 : _a2.equals(x.lessThan));
    })) {
      const values = _.uniqWith(
        ranges.map((range) => {
          var _a2;
          return (_a2 = range.greaterThan) == null ? void 0 : _a2.value;
        }),
        (a, b) => stringCompareTo(a, b) === 0
      );
      return { result: values, valuesIncluded: true };
    } else {
      const lastValue = (_b = (_a = ranges[ranges.length - 1]) == null ? void 0 : _a.greaterThan) == null ? void 0 : _b.value;
      const values = ranges.slice(0, ranges.length - 1).map((range) => {
        var _a2;
        return (_a2 = range.lessThan) == null ? void 0 : _a2.value;
      });
      const result = _.unionWith(values, [lastValue], (a, b) => stringCompareTo(a, b) === 0);
      return { result, valuesIncluded: false };
    }
  }
  /// <summary>
  /// Flattens a filter expression to the set of included boolean values.
  /// </summary>
  /// <param name="parseTree">Parsed filter set expression.</param>
  /// <param name="errors">(Optional) Collection where errors are accumulated.</param>
  /// <returns>Sequence of value ranges included by the filter expression.</returns>
  static getBooleanValue(parseTree, errors = null) {
    if (parseTree === null) {
      throw new Error("ParseTree cannot be null.");
    }
    const operators = new FilterExpressionOperatorsListener();
    ParseTreeWalker.DEFAULT.walk(operators, parseTree);
    if (operators.logicalOperatorsCount > 0 || operators.notEqualsOperatorsCount > 0 || operators.rangeOperatorsCount > 0) {
      errors = this.addOrThrow(errors, "Boolean filter expressions can only use '==' and 'IN' operators.");
    }
    const visitor = new FilterRangesVisitor(tryGetBoolean, booleanCompareTo, []);
    const range = _.uniq(parseTree.accept(visitor))[0];
    errors = this.addOrThrowMultiple(errors, visitor.errors);
    return range.greaterThan.value;
  }
  /// <summary>
  /// Flattens a filter expression to the set of included version ranges.
  /// </summary>
  /// <param name="parseTree">Parsed filter set expression.</param>
  /// <param name="errors">(Optional) Collection where errors are accumulated.</param>
  /// <returns>Sequence of value ranges included by the filter expression.</returns>
  static getVersionRanges(parseTree, errors = null) {
    if (parseTree == null) {
      throw new Error("ParseTree cannot be null.");
    }
    const visitor = new FilterRangesVisitor(tryGetVersion, versionCompareTo, []);
    const results = _.uniq(parseTree.accept(visitor));
    errors = this.addOrThrowMultiple(errors, visitor.errors);
    return results;
  }
  /// <summary>
  /// Flattens a filter expression to the set of included integer ranges.
  /// </summary>
  /// <param name="parseTree">Parsed filter set expression.</param>
  /// <param name="errors">(Optional) Collection where errors are accumulated.</param>
  /// <returns>Sequence of value ranges included by the filter expression.</returns>
  static getIntegerRanges(parseTree, errors = null) {
    if (parseTree === null) {
      throw new Error("ParseTree cannot be null.");
    }
    const visitor = new FilterRangesVisitor(tryGetInteger, numberCompareTo, []);
    const results = _.uniq(parseTree.accept(visitor));
    errors = this.addOrThrowMultiple(errors, visitor.errors);
    return results;
  }
  static getParser(filterExpression) {
    const charStream = new ANTLRInputStream(filterExpression);
    const lexer = new FilterExpressionLexer(charStream);
    const tokens = new CommonTokenStream(lexer);
    return new FilterExpressionParser(tokens);
  }
  static addOrThrow(collector, errorMessage) {
    const e = new Error(errorMessage);
    if (collector === null) {
      throw e;
    } else {
      return collector.concat([e]);
    }
  }
  static addOrThrowMultiple(collector, errors) {
    if (errors.length > 0 && collector === null) {
      throw new Error(errors.join(" "));
    } else {
      return (collector || []).concat(errors);
    }
  }
}
export {
  FilterExpression
};
//# sourceMappingURL=FilterExpression.js.map
